/// <reference types="react-scripts" />
import { Alert, Box, Checkbox, FormControlLabel, Grid } from "@mui/material";
import downloadjs from "downloadjs";
import * as React from "react";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { ConfirmationDialogResult, FileType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { SaveAttachmentBase64 } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/saveAttachmentBase64";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import { FrontPageImages, IFrontPageImages } from "../../../models/front/frontPageImages";
import * as attachmentService from "../../../models/services/attachmentService";
import * as frontService from "../../../models/services/frontService";
import { AttachmentList } from "../../attachments/AttachmentList";
import { DashboardCardProgress } from "../../framework/DashboardCard/DashboardCardProgress";
import { FilterButtons } from "../../framework/Filters/FilterButtons";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { PageDrawer } from "../../framework/Page/PageDrawer";
import { AppSettingsIcon } from "source/components/framework/Icon/AppSettingsIcon";
import { frontSetImages } from "source/models/store/front";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";

//FrontImagePropertiesDialog
export interface IFrontImagePropertiesDialogProp {
    classes?: string;
    panelSize: number;
    panelVisible: boolean;
    images: IFileAttachment[];
    imageChangeInterval: number;
    defaultImage: string;
    onSavePanelSize: (panelSize: number, panelVisible: boolean) => void;
    onClose: () => void;
}

export interface IFrontImagePropertiesDialogState {
    panelSize: string;
    panelVisible: boolean;
    images: IFileAttachment[];
    imageChangeInterval: string;
    removedImageIds: string[];
}

export class FrontImagePropertiesDialog extends React.Component<IFrontImagePropertiesDialogProp, IFrontImagePropertiesDialogState> {
    private orgStateHash: string = "";

    constructor(props: IFrontImagePropertiesDialogProp) {
        super(props);
        this.state = {
            panelSize: props.panelSize.toString(10),
            panelVisible: props.panelVisible,
            images: props.images.slice(0),
            imageChangeInterval: props.imageChangeInterval.toString(10),
            removedImageIds: []
        };
        this.getSaveDataFromState(this.state)
            .then(saveData => {
                this.orgStateHash = saveData.hash;
            });
    }

    changePanelVisibility = (panelVisible: boolean) => {
        this.setState({ panelVisible });
    };

    handleRemove = (id: string) => {
        if (!id) return;
        const state = this.state;
        const images = state.images.filter(i => i.id !== id);
        const removedImageIds = state.removedImageIds.slice(0);
        removedImageIds.push(id);
        this.setState({ images, removedImageIds });
    };

    handleDownload = async (id: string) => {
        if (!id) return;
        const state = this.state;
        const image = state.images.find(i => i.id === id);
        if (!image) return;
        if (image.file) {
            downloadjs(image.file, image.name);
            return;
        }
        if (image.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    handleAdd = async (fileList: FileList) => {
        const attachments = await FileAttachment.addFileListToFileAttachments(this.state.images, fileList, 0);
        const imageAttachments = attachments.filter(i => i.type === FileType.Image);
        this.setState({
            images: imageAttachments.sort((i, j) => Base.strCompare(i.name, j.name))
        });
    };

    getSaveDataFromState = async (state: IFrontImagePropertiesDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("imageChangeInterval", state.imageChangeInterval.toInteger());
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(state.images.filter(i => !!i.file), "photo"));
        result.append("removedImageIds", state.removedImageIds);
        //Only for change detection
        result.append("panelSize", state.panelSize.toInteger());
        result.append("panelVisible", state.panelVisible);
        return result;
    };

    saveToDb = async () => {
        const obj = this;
        const props = this.props;
        const state = this.state;
        const saveData = await obj.getSaveDataFromState(state);
        //Call server
        const result = await AppUtils.callService(() => frontService.saveImages(saveData.obj), false);
        if (!result) return;
        //Set app state
        const frontPageImages = new FrontPageImages();
        frontPageImages.imageChangeInterval = state.imageChangeInterval.toInteger();
        frontPageImages.images = state.images;
        frontPageImages.defaultImage = props.defaultImage;
        store.customStore.dispatch(frontSetImages(frontPageImages));
        //Close dialog
        props.onClose();
        //Save panel size
        props.onSavePanelSize(state.panelSize.toInteger(), state.panelVisible);
    };

    handleSave = () => {
        this.saveToDb();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(state)).hash, this.orgStateHash);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.saveToDb();
        } else {
            props.onClose();
        }
    };

    render() {
        const state = this.state;
        return (
            <PageDrawer
                open
                icon={<AppSettingsIcon />}
                title={Translations.Images}
                parentTitle={Translations.Panels}
                onClose={this.handleCancel}
            >
                <Box display="flex" flexDirection="column" flexGrow={2} margin="24px">
                    <Box display="flex" flexDirection="column" flexGrow={2}>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormControlLabel
                                    label={Translations.PanelVisible}
                                    control={
                                        <Checkbox
                                            checked={state.panelVisible}
                                            onChange={(_e, checked) => this.changePanelVisibility(checked)}
                                        />
                                    }
                                />
                            </Grid>
                        </GridRow>
                        <Alert severity="info">{Translations.PicturesAreVisibleForAllCompanyUsers}</Alert>
                        <GridRow>
                            <Grid item sm={6}>
                                <FormField title={Translations.PanelSize}>
                                    <IdTitleDropdown
                                        selectedId={state.panelSize}
                                        items={[
                                            { id: "1", title: Translations.PanelSize1 },
                                            { id: "2", title: Translations.PanelSize2 },
                                            { id: "5", title: Translations.PanelSize5 },
                                            { id: "3", title: Translations.PanelSize3 },
                                            { id: "4", title: Translations.PanelSize4 },
                                            { id: "6", title: Translations.PanelSize6 }
                                        ]}
                                        onChange={(panelSize) => this.setState({ panelSize })}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={6}>
                                <FormField title={Translations.ImageChangeInterval}>
                                    <IdTitleDropdown
                                        selectedId={state.imageChangeInterval}
                                        items={[
                                            { id: "0", title: "0 s" },
                                            { id: "10", title: "10 s" },
                                            { id: "30", title: "30 s" },
                                            { id: "60", title: "1 min" },
                                            { id: "300", title: "5 min" },
                                            { id: "600", title: "10 min" },
                                            { id: "1800", title: "30 min" },
                                            { id: "3600", title: "1 h" }
                                        ]}
                                        onChange={(imageChangeInterval) => this.setState({ imageChangeInterval })}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <AttachmentList
                            title={Translations.Images}
                            attachments={state.images}
                            onRemoveAttachment={this.handleRemove}
                            onDownloadAttachment={this.handleDownload}
                            onAddAttachments={this.handleAdd}
                        />
                    </Box>
                    <FilterButtons
                        chartPanelPropertiesMode
                        onCancel={this.handleCancel}
                        onSave={this.handleSave}
                    />
                </Box>
            </PageDrawer>
        );
    }
}

// FrontImages
export interface IFrontImagesProp {
    panelSize: number;
    panelVisible: boolean;
    images: IFrontPageImages;
    showPropertiesButton?: boolean;
    onSavePanelSize: (panelSize: number, panelVisible: boolean) => void;
}

interface IFrontImagesState {
    isSaving: boolean;
    images: IFileAttachment[];
    imageChangeInterval: number;
    imageIndex: number;
    showImagePropertiesDialog: boolean;
}

export class FrontImages extends React.Component<IFrontImagesProp, IFrontImagesState> {
    private refreshTimer = 0;

    clearRefreshTimer = () => {
        if (this.refreshTimer !== 0) {
            window.clearTimeout(this.refreshTimer);
            this.refreshTimer = 0;
        }
    };

    setRefreshTimer = (imageChangeInterval: number) => {
        const obj = this;
        this.clearRefreshTimer();
        if (!imageChangeInterval) return;
        this.refreshTimer = window.setTimeout(() => {
            const state = this.state;
            if (!this.props.images || this.props.images?.images?.length < 1) {
                obj.clearRefreshTimer();
                return;
            }
            obj.setState({
                imageIndex: state.images.length > 0 ? ((state.imageIndex + 1) % state.images.length) : -1
            });
            obj.setRefreshTimer(imageChangeInterval);
        }, this.state.imageChangeInterval * 1000);
    };

    constructor(props: IFrontImagesProp) {
        super(props);
        const images = props.images && props.images.images ? props.images.images.slice(0) : [];
        this.state = {
            isSaving: false,
            images,
            imageChangeInterval: props.images && props.images.imageChangeInterval ? props.images.imageChangeInterval : 0,
            imageIndex: images.length > 0 ? 0 : -1,
            showImagePropertiesDialog: false
        };
    }

    componentDidUpdate(prevProps: IFrontImagesProp, prevState: IFrontImagesState) {
        const props = this.props;
        if (!props.images) return;
        const prevHash = Base.joinSuffix(prevProps.images && prevProps.images.images ? prevProps.images.images.map(i => i.id) : [], "#") + (prevProps.images && prevProps.images.imageChangeInterval ? prevProps.images.imageChangeInterval : 0).toString(10);
        const curHash = Base.joinSuffix(props.images && props.images.images ? props.images.images.map(i => i.id) : [], "#") + (props.images && props.images.imageChangeInterval ? props.images.imageChangeInterval : 0).toString(10);
        if (prevHash === curHash) return;
        const imageChangeInterval = props.images && props.images.imageChangeInterval ? props.images.imageChangeInterval : 0;
        this.setState({
            imageIndex: props.images.images.length > 0 ? 0 : -1,
            images: props.images.images.slice(0),
            imageChangeInterval
        });
        this.setRefreshTimer(imageChangeInterval);
    }

    componentDidMount(): void {
        this.setRefreshTimer(this.state.imageChangeInterval);
    }

    componentWillUnmount(): void {
        this.clearRefreshTimer();
    }

    handleShowProperties = () => {
        this.setState({ showImagePropertiesDialog: true });
    };

    handleFrontImagePropertiesDialogClose = () => {
        this.setState({
            showImagePropertiesDialog: false,
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Box sx={{
                position: "relative",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 1rem",
            }}>
                {state.images.map((images, i) =>
                    <Box
                        component="img"
                        key={images.id}
                        src={images.thumbnail}
                        sx={{
                            position: "absolute",
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            opacity: (state.imageIndex === i) ? 1 : 0,
                            transition: "opacity 1s ease-in-out",
                        }}
                    />
                )}
                {state.imageIndex < 0 && !!props.images && props.images.images.length < 1 &&
                    <Box
                        component="img"
                        src={props.images.defaultImage}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                        }}
                    />
                }
                <DashboardCardProgress isLoading={state.imageIndex < 0 && !props.images} />
                {!!props.showPropertiesButton &&
                    <ToolButton
                        tooltip={Translations.Properties}
                        classes={"properties"}
                        onClick={this.handleShowProperties}
                    />
                }
                {state.showImagePropertiesDialog &&
                    <FrontImagePropertiesDialog
                        panelSize={props.panelSize}
                        panelVisible={props.panelVisible}
                        images={state.images}
                        imageChangeInterval={state.imageChangeInterval}
                        defaultImage={props.images.defaultImage}
                        onSavePanelSize={props.onSavePanelSize}
                        onClose={this.handleFrontImagePropertiesDialogClose}
                    />
                }
            </Box>
        );
    }
}